// eslint-disable-next-line import/no-extraneous-dependencies
import type _ from "jquery";
// This file is purposefully updated by hand to remove the reliance on a compilation step for other libs.

import type { TypeGuardFn } from "./TypeGuardFn.js";


/** Type: A value that is null or undefined */
export type Nil = null | undefined;

/** Type: A value that is not null or undefined */
export type NotNil<T> = T extends Nil ? never : T;

/** Type: A value that is allowed to be of type T or null or undefined */
export type Nullable<T> = T | Nil;


/** Type: Make all properties in T optional or undefined */
export type PartialWithUndefined<T> = {
    [ P in keyof T ]?: T[ P ] | undefined;
};


/** Type: Make all properties in T optional or Nil */
export type PartialWithNil<T> = {
    [ P in keyof T ]?: T[ P ] | Nil;
};

/** Type: The union of values from an "as const" object */
export type ValueOf<T extends Record<keyof T, any>> = T[ keyof T ];







export type DefinedPrimitive = string | number | symbol | boolean | bigint;

export type Primitive = string | number | symbol | boolean | bigint | null | undefined;


function objToString(value) {
    return Object.prototype.toString.call(value);
}

/** Type Guard: Checks if a value is null */
export function isNull (value: unknown): value is null {
    return value === null;
}

/** Type Guard: Checks if a value is undefined */
export function isUndefined (value: unknown): value is undefined {
    return value === undefined;
}

/** Type Guard: Checks if a value is null or undefined */
export function isNil (value: unknown): value is Nil {
    return value == null;
}

/** Type Guard: Checks if a value is not null or undefined */
export function isNotNil<T> (value: T): value is NotNil<T> {
    return value != null;
}

/** Type Guard: Checks if a value is a string */
export function isString (value: unknown): value is string {
    return typeof value === 'string'; 
}

/** Type Guard: Checks if a value is a symbol */
export function isSymbol (value: unknown): value is symbol {
    return typeof value === "symbol";
}

/** Type Guard: Checks if a value is an object */
export function isObject (value: unknown): value is object {
    return value != null && (typeof value === 'object');
}

/** Type Guard: Checks if a value is a Date object */
export function isDate (value: unknown): value is Date{
    return isObject(value) && value instanceof Date;
}

/** Type Guard: Checks if a value is a function */
export function isFunction (value: unknown): value is ((...args: any[]) => any) {
    return typeof value === 'function';
}


/** Type Guard: Checks if a value is a RegExp */
export function isRegExp (value: unknown): value is RegExp {
    return isObject(value) && (objToString(value) === '[object RegExp]' || (value instanceof RegExp));
}


/** Type Guard: Checks if a value is a boolean */
export function isBoolean (value: unknown): value is boolean {
    return value === true || value === false;
}

/** Type Guard: Checks if a value is a number */
export function isNumber (value: unknown): value is number {
    return typeof value === 'number';
}


/** Type Guard: Checks if a value is an int */
export function isInt (value: unknown): value is number {
    return isNumber(value) && (value % 1) === 0;
}

/** Type Guard: Checks if a value is a number */
export function isUint (value: unknown): value is number {
    return isInt(value) && value > -1;
}



/** Type Guard: Checks if an object has a property with TPropType */
export function hasPropertyWithType<TPropType, TPropKey extends PropertyKey, TValueType extends object> (
    value: object,
    prop: TPropKey,
    typeCheckFn: TypeGuardFn<TPropType>
): value is (TValueType & Record<TPropKey, TValueType>) {
    return isObject(value) && (prop in value) && typeCheckFn(value[prop as keyof typeof value]);
}


/** Type Guard: Checks if a value is of type PropertyKey */
export function isPropertyKey (value: unknown): value is PropertyKey {
    return isString(value) || isUint(value) || isSymbol(value);
}

function getTypeofName(value){
    return (value === null)? "null" : (typeof value);
}

export function isPrimitive (value: unknown): value is Primitive{
    const typeName = getTypeofName(value);
    return typeName !== "object" && typeName !== "function";
}


export function isDefinedPrimitive (value: unknown): value is DefinedPrimitive{
    return (value == null)? false : isPrimitive(value);
}


/** Type Guard: Checks if a value is of type JQuery<HTMLElement> */
export function isJQueryElement (value: unknown): value is JQuery<HTMLElement> {
    if ("jQuery" in globalThis) {
        const jQuery = globalThis[ "jQuery" as keyof typeof globalThis ];
        return value instanceof jQuery;
    }
    return false;
}